import { Injectable } from '@angular/core';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Equipment } from '@polaris-customerweb/sharedcomponents.garage';
import {
    catchError,
    exhaustMap,
    filter,
    map,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import { EquipmentResourceService } from './equipment-resource.service';
import * as EquipmentActions from './equipment.actions';
import { selectUpdateEquipmentLoading } from './equipment.selectors';

@Injectable()
export class EquipmentEffects {
    readonly className = 'EquipmentEffects';

    updateEquipment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EquipmentActions.UpdateEquipment),
            switchMap(({ equipment }) =>
                this.equipmentResourceService
                    .updateEquipment(equipment as Equipment)
                    .pipe(
                        map(() =>
                            EquipmentActions.UpdateEquipmentSuccess({
                                equipment: equipment as Equipment,
                            })
                        ),
                        catchError((errorMessage) => [
                            EquipmentActions.UpdateEquipmentFail({
                                errorMessage,
                            }),
                        ])
                    )
            )
        )
    );

    cancelUpdateEquipment = createEffect(() =>
        this.actions$.pipe(
            ofType(EquipmentActions.CancelUpdateEquipment),
            withLatestFrom(this.store.select(selectUpdateEquipmentLoading)),
            filter(([, isLoading]) => isLoading),
            exhaustMap(() => [
                EquipmentActions.UpdateEquipment({ equipment: undefined }),
            ])
        )
    );

    deleteEquipment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EquipmentActions.DeleteEquipment),
            exhaustMap(({ equipmentId }) =>
                this.equipmentResourceService.deleteEquipment(equipmentId).pipe(
                    map(() =>
                        EquipmentActions.DeleteEquipmentSuccess({ equipmentId })
                    ),
                    catchError((errorMessage) => [
                        EquipmentActions.DeleteEquipmentFail({
                            errorMessage,
                        }),
                    ])
                )
            )
        )
    );

    constructor(
        private readonly actions$: Actions,
        private readonly equipmentResourceService: EquipmentResourceService,
        private readonly store: Store<AppState>
    ) {}
}

export const equipmentEffectsModule = EffectsModule.forFeature([
    EquipmentEffects,
]);
